import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { AddCircleOutline, InsertDriveFileOutlined, VisibilityOutlined } from '@material-ui/icons';
import { format, parseISO } from 'date-fns';
import { Button } from '@roc/ui';
import {
  ADDENDUM_TASK_OPTION,
  ADDENDUM_TASK_TITLE,
  LoanTaskSection,
  NEW_TASK_OPTION,
  PURCHASE_CONTRACT_ADDENDUM_TASK_CODE,
  UnclassifiedDocument,
} from './unclassifiedDocumentsTypes';

const GRID_COLUMNS = 'minmax(0, 1.4fr) minmax(0, 1.1fr) minmax(0, 1.1fr) auto';

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  locked: {
    opacity: 0.55,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: 'center',
    columnGap: theme.spacing(3),
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  headerRow: {
    borderTop: 'none',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2.25, 3),
  },
  headerCell: {
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  documentCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  fileIcon: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: 6,
    backgroundColor: '#EAF1FD',
    color: '#3B6FD8',
  },
  fileInfo: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  fileName: {
    fontWeight: 600,
    fontSize: '1rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondaryText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  previewButton: {
    flex: '0 0 auto',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    color: '#3B6FD8',
  },
  select: {
    width: '100%',
    minWidth: 0,
  },
  placeholder: {
    color: theme.palette.text.disabled,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  actionButton: {
    minWidth: 100,
  },
  newTaskOption: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    gap: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const getFileExtension = (fileName: string) => {
  const extension = fileName?.split('.').pop();
  return extension && extension !== fileName ? extension.toUpperCase() : 'FILE';
};

const toDate = (value: number[] | string) => {
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    return new Date(year, month - 1, day, hour, minute, second);
  }
  return parseISO(value);
};

const formatDate = (value: number[] | string, pattern: string) => {
  try {
    return value ? format(toDate(value), pattern) : '';
  } catch {
    return '';
  }
};

export interface UnclassifiedDocumentsTableProps {
  documents: UnclassifiedDocument[];
  /** Underwriting sections of the loan (from getLoanTasks), already without parent tasks. */
  sections: LoanTaskSection[];
  /** documentId -> selected section name */
  selectedSections: Record<number, string>;
  /** documentId -> selected taskId */
  selectedTasks: Record<number, number>;
  /** documentId -> true when "Addendum for Purchase Contract" is selected (selectedTasks then holds the Purchase Contract task). */
  selectedAddendums: Record<number, boolean>;
  onSectionChange: (documentId: number, sectionName: string) => void;
  onTaskChange: (documentId: number, taskId: number, isAddendum?: boolean) => void;
  /** "Create new task…" picked in the Task dropdown of this document's row. */
  onCreateTask: (document: UnclassifiedDocument) => void;
  onPreview: (document: UnclassifiedDocument) => void;
  onSave: (document: UnclassifiedDocument) => void;
  /** Discard picked for this document (the dialog asks for confirmation). */
  onDiscard: (document: UnclassifiedDocument) => void;
  /** Disables every row while the AI is still classifying this loan's documents, or a Save is in flight. */
  locked?: boolean;
}

export const UnclassifiedDocumentsTable = ({
  documents,
  sections,
  selectedSections,
  selectedTasks,
  selectedAddendums,
  onSectionChange,
  onTaskChange,
  onCreateTask,
  onPreview,
  onSave,
  onDiscard,
  locked = false,
}: UnclassifiedDocumentsTableProps) => {
  const classes = useStyles();

  return (
    <Box className={`${classes.table} ${locked ? classes.locked : ''}`} aria-disabled={locked}>
      <Box className={`${classes.row} ${classes.headerRow}`}>
        <Typography className={classes.headerCell}>Document</Typography>
        <Typography className={classes.headerCell}>Section</Typography>
        <Typography className={classes.headerCell}>Task</Typography>
        <span />
      </Box>
      {documents.map(document => {
        const selectedSection = selectedSections[document.id] ?? '';
        const selectedTask = selectedAddendums[document.id] ? ADDENDUM_TASK_OPTION : selectedTasks[document.id] ?? '';
        const sectionTasks = sections.find(section => section.name === selectedSection)?.tasks ?? [];
        // The addendum option is only offered when this section actually has the Purchase Contract
        // task to save it to — found by task code (stable even if the task's title was renamed),
        // never by title or by the section's display name.
        const purchaseContractTask = sectionTasks.find(task => task.taskCode === PURCHASE_CONTRACT_ADDENDUM_TASK_CODE);

        return (
          <Box key={document.id} className={classes.row}>
            <Box className={classes.documentCell}>
              <Box className={classes.fileIcon}>
                <InsertDriveFileOutlined fontSize="small" />
              </Box>
              <Box className={classes.fileInfo}>
                <Typography className={classes.fileName} title={document.fileName}>
                  {document.fileName}
                </Typography>
                <Typography className={classes.secondaryText}>
                  {getFileExtension(document.fileName)} · {formatDate(document.uploadedDate, "MMM d, yyyy 'at' h:mm a")}
                </Typography>
              </Box>
              <IconButton
                className={classes.previewButton}
                disabled={locked}
                onClick={() => onPreview(document)}
                aria-label={`Preview ${document.fileName}`}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </Box>

            <FormControl variant="outlined" size="small" className={classes.select} disabled={locked}>
              <Select
                value={selectedSection}
                displayEmpty
                onChange={e => onSectionChange(document.id, e.target.value as string)}
                renderValue={value => value
                  ? value as string
                  : <span className={classes.placeholder}>Select section…</span>}
              >
                {sections.map(section => (
                  <MenuItem key={section.name} value={section.name}>
                    {section.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              className={classes.select}
              disabled={locked || !selectedSection}
            >
              <Select
                value={selectedTask}
                displayEmpty
                onChange={e => {
                  const value = e.target.value;
                  if (value === NEW_TASK_OPTION) {
                    onCreateTask(document);
                  } else if (value === ADDENDUM_TASK_OPTION) {
                    if (purchaseContractTask) onTaskChange(document.id, purchaseContractTask.taskId, true);
                  } else {
                    onTaskChange(document.id, value as number);
                  }
                }}
                renderValue={value => {
                  if (!value) return <span className={classes.placeholder}>Select task…</span>;
                  if (value === ADDENDUM_TASK_OPTION) return ADDENDUM_TASK_TITLE;
                  return sectionTasks.find(task => task.taskId === value)?.title ?? '';
                }}
              >
                {sectionTasks.reduce<JSX.Element[]>((items, task) => {
                  items.push(
                    <MenuItem key={task.taskId} value={task.taskId}>
                      {task.title}
                    </MenuItem>
                  );
                  if (task === purchaseContractTask) {
                    items.push(
                      <MenuItem key={ADDENDUM_TASK_OPTION} value={ADDENDUM_TASK_OPTION}>
                        {ADDENDUM_TASK_TITLE}
                      </MenuItem>
                    );
                  }
                  return items;
                }, [])}
                <MenuItem value={NEW_TASK_OPTION} className={classes.newTaskOption}>
                  <AddCircleOutline fontSize="small" />
                  Create new task…
                </MenuItem>
              </Select>
            </FormControl>

            <Box className={classes.actions}>
              <Button
                className={classes.actionButton}
                variant="contained"
                color="primary"
                disabled={locked || !selectedTask}
                onClick={() => onSave(document)}
                testId={`save-${document.id}`}
              >
                Save
              </Button>
              <Button
                className={classes.actionButton}
                variant="outlined"
                disabled={locked}
                onClick={() => onDiscard(document)}
                testId={`discard-${document.id}`}
              >
                Discard
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
